import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { TOKENS } from '../ui/dashboard-primitives'

/**
 * The three shapes an error-screen action can take, over one style source.
 *
 * THREE COMPONENTS RATHER THAN ONE WITH A `variant` PROP, because the split is
 * not cosmetic: `ActionLink` needs router context and `ActionAnchor` must not
 * have it. `ErrorBoundary` sits OUTSIDE `RouterProvider` (main.tsx), so a
 * `<Link>` rendered there throws inside the very boundary that is supposed to
 * be catching things. Making that a prop would let a caller pick the one that
 * crashes.
 *
 * `ActionAnchor` is also the right default for a route error even where a
 * `<Link>` would work: a plain `href` is a document load, which drops whatever
 * in-memory state broke and boots the app clean. `ActionLink` is for the 404,
 * which is a healthy page and should stay a client-side navigation.
 */

const BASE =
  'inline-flex items-center justify-center rounded-full px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.16em] no-underline outline-none transition-[transform,background-color,box-shadow] duration-150 ease-out focus-visible:ring-2 focus-visible:ring-(--accent-amber) active:scale-[0.98]'

/** The ink that reads on a filled amber pill, matching `SessionList`'s CTA. */
const AMBER_INK = '#2a2220'

const primaryStyle = { fontFamily: 'var(--font-mono)', background: TOKENS.amber, color: AMBER_INK }
const quietStyle = { fontFamily: 'var(--font-mono)', color: TOKENS.creamSoft }
const QUIET =
  `${BASE} shadow-[inset_0_0_0_1px_var(--text-cream-border)] hover:shadow-[inset_0_0_0_1px_var(--accent-amber-emphasis)]`

/** Primary action as a document load. Safe with no router above it. */
export function ActionAnchor({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className={BASE} style={primaryStyle}>
      {children}
    </a>
  )
}

/** Primary action as a client-side navigation. Requires router context. */
export function ActionLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className={BASE} style={primaryStyle}>
      {children}
    </Link>
  )
}

/** Secondary action. Outlined rather than filled, so it never rivals the primary. */
export function ActionButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className={QUIET} style={quietStyle}>
      {children}
    </button>
  )
}
